import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
  alpha,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as SparklesIcon
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { EasyWayProvider, useEasyWay } from '../contexts/EasyWayContext'
import AppHeader from '../components/common/AppHeader'
import GeneratedImageViewer from '../components/ImageGeneration/GeneratedImageViewer'
import AdvancedTools from '../components/EasyWay/AdvancedTools'

/**
 * FileUploadSlot Component - Individual slot for uploading images
 */
interface FileUploadSlotProps {
  label: string
  description: string
  file: File | null
  onFileSelect: (file: File | null) => void
  icon: React.ReactNode
}

const FileUploadSlot: React.FC<FileUploadSlotProps> = ({
  label,
  description,
  file,
  onFileSelect,
  icon
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const [dragOver, setDragOver] = useState(false)

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      onFileSelect(droppedFile)
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      onFileSelect(selectedFile)
    }
  }

  const handleClear = () => {
    onFileSelect(null)
  }

  const previewUrl = file ? URL.createObjectURL(file) : null

  return (
    <Card
      sx={{
        height: '100%',
        border: dragOver ? `2px dashed ${theme.palette.primary.main}` : '1px solid',
        borderColor: file ? theme.palette.success.main : theme.palette.divider,
        backgroundColor: dragOver
          ? alpha(theme.palette.primary.main, 0.05)
          : file
          ? alpha(theme.palette.success.main, 0.02)
          : 'background.paper',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.02)
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <CardContent sx={{ height: '100%', p: 2 }}>
        {file && previewUrl ? (
          // Preview mode
          <Box sx={{ position: 'relative', height: '100%' }}>
            <Box
              component="img"
              src={previewUrl}
              alt={label}
              sx={{
                width: '100%',
                height: 200,
                objectFit: 'contain',
                borderRadius: 1,
                mb: 1
              }}
            />
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" fontSize="small" />
              <Typography variant="body2" color="success.main" fontWeight={500}>
                {file.name}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
        ) : (
          // Upload mode
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              py: 3
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              {icon}
            </Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {label}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 2 }}
            >
              {description}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              component="label"
              startIcon={<UploadIcon />}
            >
              {t('easyWay.upload.selectFile')}
              <input
                type="file"
                hidden
                accept="image/png,image/jpeg,image/jpg,image/webp,image/avif"
                onChange={handleFileInput}
              />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {t('easyWay.upload.dragDrop')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * EasyWayPageContent - Main content component for Foto Sesja (Photo Session) page (wrapped in provider)
 */
const EasyWayPageContent: React.FC = () => {
  const { projectName } = useParams<{ projectName: string }>()
  const navigate = useNavigate()
  const { user, logout, isAdmin } = useAuth()
  const { t } = useLanguage()
  const theme = useTheme()

  const {
    modelImage,
    garmentImage,
    accessory1,
    accessory2,
    selectedModel,
    resolution,
    aspectRatio,  // NEW
    imageSize,  // NEW
    prompt,
    generationResult,
    availableModels,  // NEW
    modelsLoading,  // NEW
    setFile,
    setSelectedModel,
    setResolution,
    setAspectRatio,  // NEW
    setImageSize,  // NEW
    setPrompt,
    generateImage,
    resetState,
    isTextToImageModel  // NEW
  } = useEasyWay()

  // Advanced Tools: Track selected image for processing
  const [selectedImageForTools, setSelectedImageForTools] = useState<string | null>(null)

  // Prompt presets
  const [selectedPreset, setSelectedPreset] = useState<string>('preset1')

  const promptPresets = {
    preset1: t('easyWay.prompt.preset1'),
    preset2: t('easyWay.prompt.preset2'),
    preset3: t('easyWay.prompt.preset3')
  }

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset)
    setPrompt(promptPresets[preset as keyof typeof promptPresets])
  }

  const handleGenerate = async () => {
    const success = await generateImage(projectName || 'Default')
    if (success) {
      console.log('✅ Generation completed successfully')
      // Auto-select first generated image for Advanced Tools
      if (generationResult.previewUrls && generationResult.previewUrls.length > 0) {
        setSelectedImageForTools(generationResult.previewUrls[0])
      }
    }
  }

  const handleImageProcessed = (newUrl: string) => {
    console.log('✅ Image processed, new URL:', newUrl)
    // Update selected image to processed version
    setSelectedImageForTools(newUrl)
    // Optionally: Add to generated images list
  }

  const isGenerating = generationResult.status === 'generating'
  const hasFiles = !!(modelImage || garmentImage || accessory1 || accessory2)
  const isTextOnly = isTextToImageModel(selectedModel)
  const hasPrompt = prompt && prompt.trim().length > 0
  const canGenerate = isTextOnly ? (hasPrompt && !isGenerating) : (hasFiles && !isGenerating)

  // Generate model colors dynamically
  const modelColors: Record<string, string> = {
    'strawberry': theme.palette.error.main,
    'strawberry-pro': theme.palette.secondary.main,
    'blueberry': theme.palette.info.main,
    'kiwi': theme.palette.warning.main,
    'bear-aurora': theme.palette.primary.main,
    'melon': theme.palette.success.main
  }

  // Get model color (with fallback)
  const getModelColor = (codename: string): string => {
    return modelColors[codename] || theme.palette.grey[500]
  }

  return (
    <>
      <AppHeader
        title={`Foto Sesja - ${projectName || 'Default'}`}
        username={user?.username}
        showAdminButton={isAdmin}
        onLogout={logout}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <SparklesIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Typography variant="h4" fontWeight={600}>
              {t('easyWay.title')}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            {t('easyWay.subtitle')}
          </Typography>
        </Paper>

        {/* Info box for text-to-image models */}
        {isTextOnly && (
          <Alert severity="info" sx={{ mb: 4 }}>
            <strong>Model {availableModels[selectedModel]?.name} - Text-to-Image</strong>
            <br />
            Ten model generuje obrazy tylko na podstawie opisu tekstowego.
            Obrazy referencyjne NIE są wspierane przez xAI API.
            <br /><br />
            <strong>Przykład:</strong> "Young woman with long brown hair, wearing elegant black dress,
            professional studio lighting, white background, fashion photography style"
          </Alert>
        )}

        {/* Upload Section (only for image-to-image models) */}
        {!isTextOnly && (
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('easyWay.upload.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('easyWay.upload.subtitle')}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FileUploadSlot
                    label={t('easyWay.upload.model')}
                    description={t('easyWay.upload.modelDesc')}
                    file={modelImage}
                    onFileSelect={(file) => setFile('modelImage', file)}
                    icon={<ImageIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FileUploadSlot
                    label={t('easyWay.upload.garment')}
                    description={t('easyWay.upload.garmentDesc')}
                    file={garmentImage}
                    onFileSelect={(file) => setFile('garmentImage', file)}
                    icon={<ImageIcon sx={{ fontSize: 32, color: theme.palette.secondary.main }} />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FileUploadSlot
                    label={t('easyWay.upload.accessory1')}
                    description={t('easyWay.upload.accessory1Desc')}
                    file={accessory1}
                    onFileSelect={(file) => setFile('accessory1', file)}
                    icon={<ImageIcon sx={{ fontSize: 32, color: theme.palette.success.main }} />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FileUploadSlot
                    label={t('easyWay.upload.accessory2')}
                    description={t('easyWay.upload.accessory2Desc')}
                    file={accessory2}
                    onFileSelect={(file) => setFile('accessory2', file)}
                    icon={<ImageIcon sx={{ fontSize: 32, color: theme.palette.warning.main }} />}
                  />
                </Grid>
              </Grid>
            </Paper>
        )}

        {/* Model Selection Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('easyWay.model.title')}
          </Typography>
          {modelsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <RadioGroup
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value as any)}
            >
              {Object.entries(availableModels).map(([key, info]) => (
                <FormControlLabel
                  key={key}
                  value={key}
                  control={<Radio />}
                  disabled={info.status !== 'ready'}
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {info.name}
                        {info.input_type === 'text_only' && (
                          <Typography component="span" variant="caption" sx={{ ml: 1, color: theme.palette.info.main }}>
                            (Text-to-Image)
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {info.description}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    border: '1px solid',
                    borderColor:
                      selectedModel === key ? getModelColor(key) : theme.palette.divider,
                    borderRadius: 1,
                    p: 1.5,
                    mb: 1,
                    backgroundColor:
                      selectedModel === key ? alpha(getModelColor(key), 0.05) : 'transparent'
                  }}
                />
              ))}
            </RadioGroup>
          )}

          {/* Resolution Selector - Only for Blueberry */}
          {selectedModel === 'blueberry' && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('easyWay.resolution.title')}
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value as '2k' | '2k_portrait' | '4k' | '4k_portrait')}
                  sx={{
                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.info.main
                    }
                  }}
                >
                  <MenuItem value="2k">{t('easyWay.resolution.2k')}</MenuItem>
                  <MenuItem value="2k_portrait">{t('easyWay.resolution.2kPortrait')}</MenuItem>
                  <MenuItem value="4k">{t('easyWay.resolution.4k')}</MenuItem>
                  <MenuItem value="4k_portrait">{t('easyWay.resolution.4kPortrait')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Aspect Ratio & Resolution - For Strawberry Models */}
          {(selectedModel === 'strawberry' || selectedModel === 'strawberry-pro') && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Proporcje obrazu (Aspect Ratio)
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={aspectRatio}
                  onChange={(e) => setAspectRatio(e.target.value as any)}
                  sx={{
                    backgroundColor: alpha(
                      selectedModel === 'strawberry-pro'
                        ? theme.palette.secondary.main
                        : theme.palette.error.main,
                      0.05
                    ),
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: selectedModel === 'strawberry-pro'
                        ? theme.palette.secondary.main
                        : theme.palette.error.main
                    }
                  }}
                >
                  <MenuItem value="none">Nie zmieniaj (oryginalny)</MenuItem>
                  <MenuItem value="1:1">Kwadrat (1:1)</MenuItem>
                  <MenuItem value="16:9">Panorama (16:9)</MenuItem>
                  <MenuItem value="9:16">Pionowy (9:16)</MenuItem>
                  <MenuItem value="4:3">Standard (4:3)</MenuItem>
                  <MenuItem value="3:4">Pionowy (3:4)</MenuItem>
                  <MenuItem value="4:5">Instagram (4:5)</MenuItem>
                  <MenuItem value="5:4">Poziomy (5:4)</MenuItem>
                  <MenuItem value="2:3">Portret (2:3)</MenuItem>
                  <MenuItem value="3:2">Poziomy (3:2)</MenuItem>
                  <MenuItem value="21:9">Ultrawide (21:9)</MenuItem>
                </Select>
              </FormControl>

              {/* Image Size - Resolution Selector (Only for Strawberry Pro) */}
              {selectedModel === 'strawberry-pro' && (
                <>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Rozdzielczość (Resolution)
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={imageSize}
                      onChange={(e) => setImageSize(e.target.value as any)}
                      sx={{
                        backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.secondary.main
                        }
                      }}
                    >
                      <MenuItem value="1K">1K - Szybko (1024px)</MenuItem>
                      <MenuItem value="2K">2K - Standard (2048px)</MenuItem>
                      <MenuItem value="4K">4K - Produkcja (4096px)</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    💡 Wyższa rozdzielczość = dłuższy czas generacji (~30s dla 4K)
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Paper>

        {/* Prompt Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('easyWay.prompt.title')}
          </Typography>

          {/* Preset Selector */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('easyWay.prompt.select')}</InputLabel>
            <Select
              value={selectedPreset}
              label={t('easyWay.prompt.select')}
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              <MenuItem value="preset1">{t('easyWay.prompt.preset1')}</MenuItem>
              <MenuItem value="preset2">{t('easyWay.prompt.preset2')}</MenuItem>
              <MenuItem value="preset3">{t('easyWay.prompt.preset3')}</MenuItem>
            </Select>
          </FormControl>

          {/* Custom Prompt TextField */}
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('easyWay.prompt.custom')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            variant="outlined"
            helperText={t('easyWay.prompt.helper')}
          />
        </Paper>

        {/* Generate Button */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleGenerate}
            disabled={!canGenerate}
            startIcon={isGenerating ? <CircularProgress size={20} /> : <SparklesIcon />}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 3
            }}
          >
            {isGenerating ? t('easyWay.generating') : t('easyWay.generate')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={resetState}
            disabled={isGenerating}
            sx={{ minWidth: 120 }}
          >
            {t('easyWay.reset')}
          </Button>
        </Box>

        {/* Status Messages */}
        {generationResult.status === 'error' && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {generationResult.message || t('easyWay.error')}
          </Alert>
        )}

        {generationResult.status === 'success' && (
          <Alert severity="success" sx={{ mb: 4 }}>
            {t('easyWay.success').replace('{count}', String(generationResult.imagesGenerated))}
          </Alert>
        )}

        {/* Results Section */}
        {generationResult.status === 'success' && generationResult.previewUrls && (
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('easyWay.results')}
            </Typography>
            <GeneratedImageViewer
              images={generationResult.previewUrls.map((url) => ({
                url,
                timestamp: new Date().toISOString()
              }))}
              onImageClick={(image) => {
                console.log('Image clicked:', image)
                setSelectedImageForTools(image.url)
              }}
            />
          </Paper>
        )}

        {/* Advanced Tools Section - Always visible (collapsed by default) */}
        <AdvancedTools
          imageUrl={selectedImageForTools}
          projectName={projectName || 'Default'}
          onProcessed={handleImageProcessed}
        />
      </Container>
    </>
  )
}

/**
 * EasyWayPage - Main page component for Foto Sesja (Photo Session) with provider wrapper
 */
const EasyWayPage: React.FC = () => {
  return (
    <EasyWayProvider>
      <EasyWayPageContent />
    </EasyWayProvider>
  )
}

export default EasyWayPage
